import { Activity, TrendingUp, Target, Layers } from "lucide-react";

interface Props {
  record: { wins: number; losses: number; pushes: number } | null;
  units: number | null;
  bestEdgeLabel?: string | null;
  activeSports: string[];
}

export default function TodaySnapshot({ record, units, bestEdgeLabel, activeSports }: Props) {
  const hasAnything =
    (record && record.wins + record.losses + record.pushes > 0) ||
    (units !== null && units !== 0) ||
    bestEdgeLabel ||
    activeSports.length > 0;
  if (!hasAnything) return null;

  return (
    <div
      className="te-card relative overflow-hidden p-3.5 mb-4"
      aria-label="Today snapshot"
    >
      <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="flex items-center gap-1.5 mb-2.5">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">Live · Today</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
        {record && (record.wins + record.losses + record.pushes > 0) && (
          <Row Icon={Activity} tone="text-foreground">
            <span className="font-bold tabular-nums">{record.wins}-{record.losses}{record.pushes ? `-${record.pushes}` : ""}</span>
            <span className="text-muted-foreground ml-1">today</span>
          </Row>
        )}
        {units !== null && (
          <Row Icon={TrendingUp} tone={units >= 0 ? "text-success" : "text-danger"}>
            <span className="font-bold tabular-nums">{units >= 0 ? "+" : ""}{units.toFixed(1)}u</span>
            <span className="text-muted-foreground ml-1">tracked</span>
          </Row>
        )}
        {bestEdgeLabel && (
          <Row Icon={Target} tone="text-accent" className="col-span-2">
            <span className="text-muted-foreground">Top edge:&nbsp;</span>
            <span className="font-semibold text-foreground truncate">{bestEdgeLabel}</span>
          </Row>
        )}
        {activeSports.length > 0 && (
          <Row Icon={Layers} tone="text-foreground" className="col-span-2">
            <span className="text-muted-foreground">Active:&nbsp;</span>
            <span className="font-semibold tracking-wide">{activeSports.join(" · ")}</span>
          </Row>
        )}
      </div>
    </div>
  );
}

function Row({
  Icon,
  tone,
  className,
  children,
}: {
  Icon: any;
  tone: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex items-center gap-1.5 min-w-0 ${className ?? ""}`}>
      <Icon className={`w-3.5 h-3.5 shrink-0 ${tone}`} />
      <div className="truncate">{children}</div>
    </div>
  );
}
